import React, { Fragment } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import {
  doesEnergyGroupedCostHaveValueRows,
  doEnergyCostsHaveValue,
  groupEnergyCosts,
  mapVersionCostCalculatorsForGrouping,
} from './energy-cost-table-util'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { reduceToSum } from '../../../util/array-utility'
import { formatPrice } from '../../../util/formatters'
import { CostsTableValueRow } from './CostsTableValueRow'
import { useTableStyles } from '../../common/use-table-styles'
import { EnergyCostRow, GroupedCost } from './cost-table-types'
import { EnergyCalculationMissing } from '../../common/EnergyCalculationMissing'
import {
  dictionariesVar,
  isCostCalculationSuccessVar,
  selectedCostCalculatorsVar,
} from '../../../apollo/reactive-vars'
import { CostsTableVersionNameRow } from './CostsTableVersionNameRow'

type Props = {
  versions: SelectedVersion[]
}

export function EnergyCostsTable(props: Props): React.ReactElement {
  const { versions } = props
  const classes = useTableStyles()
  const { t } = useTranslation()
  const { containerIdsToNames } = useReactiveVar(dictionariesVar)
  const isCostCalculationSuccess = useReactiveVar(isCostCalculationSuccessVar)
  const versionCostCalculators = useReactiveVar(selectedCostCalculatorsVar)

  if (!isCostCalculationSuccess) {
    return <EnergyCalculationMissing />
  }

  const groupingInput = mapVersionCostCalculatorsForGrouping(
    versionCostCalculators,
  )
  const groupedCosts = groupEnergyCosts(
    containerIdsToNames,
    groupingInput,
    versions,
  )

  const getVersionSum = (versionId: string, costs: GroupedCost[]): string => {
    const sum = costs
      .flatMap((groupedCost) => groupedCost.costRows)
      .flatMap((row) => row.costs)
      .filter((cost) => cost.versionId === versionId)
      .map((cost) =>
        cost.multiplier ? cost.value * cost.multiplier : cost.value,
      )
      .reduce(reduceToSum, 0)

    return formatPrice(sum, { omitUnit: true })
  }

  return (
    <TableContainer>
      <Table className={classes.table}>
        <TableBody>
          {groupedCosts.length === 0 ? (
            <CostsTableVersionNameRow versions={versions} />
          ) : (
            groupedCosts.map(
              (groupedCost) =>
                doesEnergyGroupedCostHaveValueRows(groupedCost) && (
                  <Fragment key={`energy-${groupedCost.name}`}>
                    <TableRow>
                      <TableCell className={classes.titleHeaderCell}>
                        {groupedCost.name
                          ? groupedCost.name
                          : t('costs.table.noProvider')}
                      </TableCell>
                      {versions.map((version) => (
                        <TableCell
                          className={classes.versionHeaderCell}
                          key={`energy-${version.id}`}
                          align="center"
                        >
                          {version.name}
                        </TableCell>
                      ))}
                    </TableRow>
                    {groupedCost.costRows.map(
                      (costRow: EnergyCostRow) =>
                        doEnergyCostsHaveValue(costRow.costs) && (
                          <CostsTableValueRow
                            key={`energy-${costRow.translationKey}`}
                            costRow={costRow}
                            versions={versions}
                            type="energy"
                            title={groupedCost.name}
                          />
                        ),
                    )}
                  </Fragment>
                ),
            )
          )}
          <TableRow>
            <TableCell className={classes.titleHeaderCell}>
              {t('costs.table.sum')}
            </TableCell>
            {versions.map((version) => (
              <Fragment key={`sum-${version.id}`}>
                <TableCell className={classes.versionHeaderCell} align="center">
                  {getVersionSum(version.id, groupedCosts)}
                </TableCell>
              </Fragment>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
